import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Linkedin, Twitter, Instagram, Youtube, Mail, Phone, MapPin, ArrowRight, ChevronDown, Globe } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [companyInfo, setCompanyInfo] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/company-info`);
        if (response.ok) {
          const data = await response.json();
          setCompanyInfo(data);
          // Set default to primary address's country or first available
          if (data.addresses && data.addresses.length > 0) {
            const primaryAddr = data.addresses.find(a => a.is_primary) || data.addresses[0];
            setSelectedCountry(primaryAddr.country || primaryAddr.label);
          }
        }
      } catch (error) {
        console.error('Failed to fetch company info:', error);
      }
    };
    
    const fetchServices = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/service-menu?active_only=true`);
        if (response.ok) {
          const data = await response.json();
          setServices(data);
        }
      } catch (error) {
        console.error('Failed to fetch services:', error);
      }
    };
    
    fetchCompanyInfo();
    fetchServices();
  }, []);

  const addresses = companyInfo?.addresses || [];
  const phones = companyInfo?.phones || [];
  
  // Get unique countries from addresses
  const countries = [...new Set(addresses.map(a => a.country || a.label))];
  
  // Get address and phones for selected country
  const currentAddress = addresses.find(a => (a.country || a.label) === selectedCountry);
  const currentPhones = phones.filter(p => (p.country || 'India') === selectedCountry);
  
  // If no phones for selected country, show all phones
  const displayPhones = currentPhones.length > 0 ? currentPhones : phones;

  return (
    <footer className="relative overflow-hidden bg-[#0a0a1a]">
      {/* World Map Background */}
      <div className="absolute inset-0 opacity-[0.08]">
        <svg viewBox="0 0 1200 600" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
          {/* Simplified World Map Path */}
          <g fill="currentColor" className="text-white">
            {/* North America */}
            <path d="M150,180 Q180,150 220,160 L260,140 Q300,130 340,145 L380,160 Q400,180 390,210 L370,250 Q350,280 320,290 L280,295 Q250,290 230,270 L200,240 Q170,210 150,180 Z" />
            {/* South America */}
            <path d="M250,320 Q280,310 300,330 L320,370 Q330,410 320,450 L300,490 Q280,510 260,500 L240,460 Q230,420 235,380 L250,320 Z" />
            {/* Europe */}
            <path d="M480,140 Q520,130 560,145 L590,160 Q610,175 600,200 L580,220 Q560,235 530,230 L500,215 Q475,195 480,170 L480,140 Z" />
            {/* Africa */}
            <path d="M500,250 Q540,240 570,260 L600,300 Q620,350 600,400 L570,440 Q540,460 510,440 L490,400 Q470,350 480,300 L500,250 Z" />
            {/* Asia */}
            <path d="M620,120 Q680,100 750,115 L820,140 Q880,160 920,200 L940,250 Q950,300 920,340 L870,370 Q820,390 760,380 L700,350 Q650,310 640,260 L630,200 Q620,160 620,120 Z" />
            {/* Australia */}
            <path d="M850,400 Q890,390 920,410 L950,440 Q970,470 960,500 L930,520 Q890,530 860,510 L840,480 Q830,450 840,420 L850,400 Z" />
            {/* Connection dots for major cities */}
            <circle cx="200" cy="200" r="4" className="animate-pulse" />
            <circle cx="520" cy="180" r="4" className="animate-pulse" />
            <circle cx="750" cy="220" r="4" className="animate-pulse" />
            <circle cx="900" cy="450" r="4" className="animate-pulse" />
            <circle cx="280" cy="400" r="4" className="animate-pulse" />
            <circle cx="550" cy="350" r="4" className="animate-pulse" />
            {/* Grid lines */}
            <line x1="0" y1="150" x2="1200" y2="150" stroke="currentColor" strokeWidth="0.5" strokeDasharray="5,10" opacity="0.3" />
            <line x1="0" y1="300" x2="1200" y2="300" stroke="currentColor" strokeWidth="0.5" strokeDasharray="5,10" opacity="0.3" />
            <line x1="0" y1="450" x2="1200" y2="450" stroke="currentColor" strokeWidth="0.5" strokeDasharray="5,10" opacity="0.3" />
            <line x1="300" y1="0" x2="300" y2="600" stroke="currentColor" strokeWidth="0.5" strokeDasharray="5,10" opacity="0.3" />
            <line x1="600" y1="0" x2="600" y2="600" stroke="currentColor" strokeWidth="0.5" strokeDasharray="5,10" opacity="0.3" />
            <line x1="900" y1="0" x2="900" y2="600" stroke="currentColor" strokeWidth="0.5" strokeDasharray="5,10" opacity="0.3" />
          </g>
        </svg>
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#320547]/20 via-transparent to-[#0a0a1a]" />

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* Logo & Description - Span 3 columns */}
          <div className="lg:col-span-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_mobittech/artifacts/hyzx3sw9_Frame%202%20%281%29.png" 
              alt="Navaris Digital" 
              className="h-10 w-auto brightness-0 invert mb-4"
            />
            <p className="text-gray-400 text-base mb-6 leading-relaxed">
              {companyInfo?.tagline || 'Transforming Ideas Into Digital Excellence'}
            </p>
            
            {/* Social Icons */}
            <div className="flex gap-2">
              {companyInfo?.linkedin && (
                <a href={companyInfo.linkedin} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center hover:bg-[#0077B5] transition-all duration-300 group">
                  <Linkedin size={16} className="text-gray-400 group-hover:text-white" />
                </a>
              )}
              {companyInfo?.twitter && (
                <a href={companyInfo.twitter} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center hover:bg-[#1DA1F2] transition-all duration-300 group">
                  <Twitter size={16} className="text-gray-400 group-hover:text-white" />
                </a>
              )}
              {companyInfo?.instagram && (
                <a href={companyInfo.instagram} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center hover:bg-pink-600 transition-all duration-300 group">
                  <Instagram size={16} className="text-gray-400 group-hover:text-white" />
                </a>
              )}
              {companyInfo?.youtube && (
                <a href={companyInfo.youtube} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center hover:bg-red-600 transition-all duration-300 group">
                  <Youtube size={16} className="text-gray-400 group-hover:text-white" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links - Span 2 columns */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-semibold mb-5 text-base uppercase tracking-wider">Quick Links</h4>
            <ul className="space-y-3">
              {['Home', 'About', 'Services', 'Industries', 'Careers', 'Contact'].map((item) => (
                <li key={item}>
                  <Link
                    to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                    className="text-gray-400 hover:text-white text-base flex items-center gap-2 group transition-colors"
                  >
                    <ArrowRight size={14} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200" />
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services - Span 2 columns */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-semibold mb-5 text-base uppercase tracking-wider">Services</h4>
            <ul className="space-y-3">
              {services.slice(0, 5).map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/services/${service.slug}`}
                    className="text-gray-400 hover:text-white text-base flex items-center gap-2 group transition-colors"
                  >
                    <ArrowRight size={14} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info with Country Selector - Span 5 columns */}
          <div className="lg:col-span-5">
            <div className="flex items-center justify-between mb-5">
              <h4 className="text-white font-semibold text-base uppercase tracking-wider flex items-center gap-2">
                <Globe size={18} className="text-[#95228A]" />
                Contact Us
              </h4>
              
              {/* Country Dropdown */}
              {countries.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:bg-white/10 transition-all"
                    data-testid="country-selector"
                  >
                    <MapPin size={14} className="text-[#95228A]" />
                    <span>{selectedCountry}</span>
                    <ChevronDown 
                      size={14} 
                      className={`text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  <AnimatePresence>
                    {isDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 top-full mt-2 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-2xl overflow-hidden z-50 min-w-[150px]"
                      >
                        {countries.map((country, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              setSelectedCountry(country);
                              setIsDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-white/10 transition-colors flex items-center gap-2 ${
                              selectedCountry === country ? 'bg-[#320547]/50 text-white' : 'text-gray-300'
                            }`}
                            data-testid={`country-option-${country}`}
                          >
                            <MapPin size={12} className={selectedCountry === country ? 'text-[#95228A]' : 'text-gray-500'} />
                            {country}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Contact Details Grid */}
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedCountry}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                {/* Address Card */}
                {currentAddress && (
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all duration-300">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#320547] flex items-center justify-center flex-shrink-0">
                        <MapPin size={18} className="text-white" />
                      </div>
                      <div>
                        <span className="text-[#95228A] text-xs font-medium block mb-1">{currentAddress.label}</span>
                        <p className="text-gray-300 text-sm leading-relaxed">
                          {currentAddress.address}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Phone Cards */}
                {displayPhones.map((phone, idx) => (
                  <a
                    key={idx}
                    href={`tel:${phone.number}`}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all duration-300 group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#95228A] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <Phone size={18} className="text-white" />
                      </div>
                      <div>
                        <span className="text-[#95228A] text-xs font-medium block mb-1">{phone.label}</span>
                        <span className="text-white font-medium">{phone.number}</span>
                      </div>
                    </div>
                  </a>
                ))}

                {/* Email Card */}
                {companyInfo?.email && (
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all duration-300 group md:col-span-2"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#320547] to-[#95228A] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <Mail size={18} className="text-white" />
                      </div>
                      <div>
                        <span className="text-[#95228A] text-xs font-medium block mb-1">Email Us</span>
                        <span className="text-white font-medium">{companyInfo.email}</span>
                      </div>
                    </div>
                  </a>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-base">
            © {currentYear} {companyInfo?.company_name || 'Navaris Digital'}. All rights reserved.
          </p>
          <div className="flex gap-6 text-base">
            <Link to="/privacy" className="text-gray-500 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-500 hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
